import * as z from "zod";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/http";

export const CREDENTIAL_TYPES = [
  "EMAIL_CONFIG",
  "SLACK",
  "OPENAI",
  "DEEPSEEK",
  "GEMINI",
] as const;
export const credentialTypeSchema = z.enum(CREDENTIAL_TYPES);
export type CredentialType = z.infer<typeof credentialTypeSchema>;

export const CREDENTIAL_TYPES_DISPLAY = {
  EMAIL_CONFIG: "Email (SMTP)",
  SLACK: "Slack",
  OPENAI: "OpenAI",
  DEEPSEEK: "DeepSeek",
  GEMINI: "Gemini",
} as const satisfies Record<CredentialType, string>;

export const credentialSchema = z.object({
  id: z.number(),
  type: credentialTypeSchema,
  config_enc: z.base64url(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime().nullable(),
  deleted_at: z.iso.datetime().nullable(),
});
export type Credential = z.infer<typeof credentialSchema>;

export const credentialFormSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("EMAIL_CONFIG"),
    config: z.object({
      username: z.string().min(1),
      password: z.string().min(1),
      port: z.number().int().positive(),
      server: z.string().min(1),
      starttls: z.boolean().default(true),
      ssl_tls: z.boolean().default(false),
      sender: z.email(),
    }),
  }),
  z.object({
    type: z.literal("SLACK"),
    config: z.object({ token: z.string().min(1) }),
  }),
  z.object({
    type: z.enum(["OPENAI", "DEEPSEEK", "GEMINI"]),
    config: z.object({
      api_key: z.string().min(1),
    }),
  }),
]);
export type CredentialFormData = z.infer<typeof credentialFormSchema>;

export function credentialsListQueryOptions() {
  return queryOptions({
    queryKey: ["credentials", "list"],
    queryFn: api.credentials.list,
  });
}

export function useCredentialsList() {
  return useQuery(credentialsListQueryOptions());
}
